/**
 * The floating support bubble.
 *
 * One click opens the message box, focused and empty — nothing to choose, no
 * form. Starting a conversation is the thing being optimised for, and every
 * decision placed before the box costs conversations.
 *
 * Nothing is attached on the way in. A picture of the page the reporter
 * happens to be standing on is rarely the page they mean, and one arriving
 * uninvited reads as the widget helping itself to the screen — so the
 * paperclip offers the camera, and the reporter decides.
 *
 * The bubble stays put while the panel is open and becomes its close button.
 * A launcher that vanishes on click leaves nothing anchoring the panel and
 * nothing to press to get rid of it — every messenger keeps it and toggles.
 */
import CloseIcon from "@mui/icons-material/Close";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import Badge from "@mui/material/Badge";
import Fab from "@mui/material/Fab";
import { useTheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { fullScreenPanelQuery } from "./support-panel-layout";
import { CAPTURE_UI_PROPS, useSupportReport } from "./use-support-report";

const SupportCaptureBubble: React.FC = () => {
	const { openReport, closeReport, isOpen, unreadCount } = useSupportReport();
	const theme = useTheme();
	const isPanelFullScreen = useMediaQuery(fullScreenPanelQuery(theme));

	return (
		<Tooltip
			title={
				isOpen ? "Close" : unreadCount > 0 ? "Support has replied" : "Get help"
			}
		>
			<Fab
				color="primary"
				size="medium"
				onClick={() => (isOpen ? closeReport() : openReport())}
				data-component="support-capture-fab"
				sx={{
					position: "fixed",
					right: 24,
					bottom: 24,
					zIndex: theme.zIndex.speedDial,
					// Where the panel fills the screen the bubble would sit on
					// top of it, over the composer; there the panel's own close
					// is the way out. Tied to the panel's own query rather than
					// to `xs`, because a phone in landscape is wide enough to
					// miss `xs` and still gets the full-screen panel.
					display: isOpen && isPanelFullScreen ? "none" : "inline-flex",
				}}
				{...CAPTURE_UI_PROPS}
			>
				{isOpen ? (
					<CloseIcon />
				) : (
					<Badge
						color="error"
						badgeContent={unreadCount}
						data-component="support-bubble-unread"
					>
						<SupportAgentIcon />
					</Badge>
				)}
			</Fab>
		</Tooltip>
	);
};

export default SupportCaptureBubble;
